import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send } from "lucide-react";
import { api, apiErr } from "@/lib/api";

export default function Contact() {
  const [c, setC] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", concern: "", preferred_time: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/content/contact").then(({ data }) => setC(data)).catch(() => {});
  }, []);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/contact-messages", form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", phone: "", concern: "", preferred_time: "" });
    } catch (err) {
      toast.error(apiErr(err, "Could not send message"));
    } finally {
      setSubmitting(false);
    }
  };

  const items = [
    { icon: MapPin, label: "Address", value: c?.address },
    { icon: Phone, label: "Phone", value: c?.phone },
    { icon: Mail, label: "Email", value: c?.email },
    { icon: MessageCircle, label: "WhatsApp", value: c?.whatsapp },
    { icon: Clock, label: "Clinic Hours", value: c?.hours },
  ].filter((i) => i.value);

  return (
    <div data-testid="contact-page">
      <section className="border-b border-border bg-secondary/40">
        <div className="container-xl py-14 md:py-20">
          <span className="eyebrow text-accent">Contact Us</span>
          <h1 className="mt-3 text-4xl font-700 sm:text-5xl">Let's talk wellness</h1>
          <p className="mt-5 max-w-2xl text-lg text-muted-foreground">{c?.note}</p>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-xl grid gap-10 lg:grid-cols-2">
          <div className="space-y-4" data-testid="contact-details">
            {items.map((it) => (
              <div key={it.label} className="flex items-start gap-4 rounded-2xl border border-border bg-card p-5">
                <div className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent"><it.icon className="h-5 w-5" /></div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-muted-foreground">{it.label}</div>
                  <div className="mt-1 font-500 text-foreground">{it.value}</div>
                </div>
              </div>
            ))}
          </div>

          <form onSubmit={submit} className="rounded-2xl border border-border bg-card p-8 shadow-sm" data-testid="contact-form">
            <h2 className="font-serif text-2xl text-[var(--brand-navy)]">Send us an enquiry</h2>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <input required name="name" value={form.name} onChange={onChange} placeholder="Your name" className="input-field" data-testid="contact-name-input" />
              <input required type="email" name="email" value={form.email} onChange={onChange} placeholder="Email address" className="input-field" data-testid="contact-email-input" />
              <input name="phone" value={form.phone} onChange={onChange} placeholder="Phone (optional)" className="input-field" data-testid="contact-phone-input" />
              <input name="preferred_time" value={form.preferred_time} onChange={onChange} placeholder="Preferred time (optional)" className="input-field" data-testid="contact-time-input" />
            </div>
            <textarea name="concern" value={form.concern} onChange={onChange} rows={4} placeholder="Tell us about your health concern..." className="input-field mt-4 resize-none" data-testid="contact-concern-input" />
            <button type="submit" disabled={submitting} className="btn-primary mt-6 w-full disabled:opacity-60" data-testid="contact-submit-button">
              {submitting ? "Sending..." : (<>Send Message <Send className="h-4 w-4" /></>)}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
}
